'use client'

import { motion } from 'framer-motion'
import { TrendingUp, Zap, Award, Wallet, MapPin, Calendar, Star } from 'lucide-react'

export default function DashboardPreview() {
  return (
    <section id="nurses" className="py-20 bg-gradient-to-b from-gray-800 to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Your <span className="text-gradient bg-gradient-to-r from-accent-400 to-primary-400">Command Center</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Everything you need in one beautiful dashboard. Track earnings, manage contracts,
            and watch your rewards grow.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Earnings Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="glass-effect rounded-3xl p-6 hover:bg-white/10 transition-all duration-300 group"
            whileHover={{ y: -5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Wallet className="w-5 h-5 text-green-400" />
                <span className="text-gray-400 text-sm">This Month</span>
              </div>
              <TrendingUp className="w-5 h-5 text-green-400" />
            </div>
            <div className="text-4xl font-bold text-white mb-2">$12,480</div>
            <div className="text-sm text-green-400">+18% from last month</div>
            <div className="mt-4 pt-4 border-t border-gray-700">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Available Now</span>
                <span className="text-white font-semibold">$3,120</span>
              </div>
            </div>
            <motion.button
              className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-green-500 to-emerald-500 rounded-xl text-white font-semibold"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Instant Payout
            </motion.button>
          </motion.div>

          {/* Loyalty Points */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2 }}
            className="glass-effect rounded-3xl p-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-500/30 hover:border-purple-500/50 transition-all duration-300 group"
            whileHover={{ y: -5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Award className="w-5 h-5 text-purple-400" />
                <span className="text-gray-400 text-sm">Rewards</span>
              </div>
              <div className="px-3 py-1 bg-purple-500/30 rounded-full text-xs text-purple-300 font-semibold">
                Gold Tier
              </div>
            </div>
            <div className="text-4xl font-bold text-gradient bg-gradient-to-r from-purple-400 to-pink-400 mb-2">
              15,240
            </div>
            <div className="text-sm text-gray-300">Points Balance</div>

            {/* Progress to next tier */}
            <div className="mt-4 pt-4 border-t border-gray-700/50">
              <div className="flex justify-between text-xs text-gray-400 mb-2">
                <span>Progress to Platinum</span>
                <span>76%</span>
              </div>
              <div className="h-2 bg-gray-700 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-purple-500 to-pink-500"
                  initial={{ width: 0 }}
                  whileInView={{ width: '76%' }}
                  viewport={{ once: true }}
                  transition={{ duration: 1, delay: 0.5 }}
                />
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="text-xs text-gray-400">Available Rewards:</div>
              <div className="flex gap-2">
                <div className="px-3 py-1 bg-white/10 rounded-lg text-xs text-white">$500 Cash</div>
                <div className="px-3 py-1 bg-white/10 rounded-lg text-xs text-white">Free CEUs</div>
                <div className="px-3 py-1 bg-white/10 rounded-lg text-xs text-white">Travel</div>
              </div>
            </div>
          </motion.div>

          {/* Active Contracts */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3 }}
            className="glass-effect rounded-3xl p-6 hover:bg-white/10 transition-all duration-300 group"
            whileHover={{ y: -5 }}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <Calendar className="w-5 h-5 text-cyan-400" />
                <span className="text-gray-400 text-sm">Current Contract</span>
              </div>
              <div className="px-2 py-1 bg-green-500/20 rounded-full">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
              </div>
            </div>
            <div className="text-2xl font-bold text-white mb-2">Cedars-Sinai</div>
            <div className="flex items-center space-x-2 text-gray-400 text-sm mb-4">
              <MapPin className="w-4 h-4" />
              <span>Los Angeles, CA</span>
            </div>

            <div className="space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Rate</span>
                <span className="text-white font-semibold">$92/hr</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Duration</span>
                <span className="text-white font-semibold">13 weeks</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Days Left</span>
                <span className="text-cyan-400 font-semibold">45 days</span>
              </div>
            </div>

            <motion.button
              className="w-full mt-4 px-4 py-2 glass-effect rounded-xl text-white font-semibold border border-white/20 hover:bg-white/10"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              View Details
            </motion.button>
          </motion.div>
        </div>

        {/* AI Recommendations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="glass-effect rounded-3xl p-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-xl">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-white">AI-Powered Recommendations</h3>
              <p className="text-gray-400">Personalized opportunities matching your preferences</p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {[
              {
                hospital: 'Mayo Clinic',
                location: 'Rochester, MN',
                specialty: 'ICU',
                rate: 98,
                match: 95,
              },
              {
                hospital: 'Johns Hopkins',
                location: 'Baltimore, MD',
                specialty: 'Cardiovascular',
                rate: 102,
                match: 92,
              },
            ].map((job, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-effect rounded-2xl p-6 hover:bg-white/10 transition-all duration-300 cursor-pointer group"
                whileHover={{ x: 5 }}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-xl font-bold text-white group-hover:text-gradient group-hover:from-accent-400 group-hover:to-primary-400">
                      {job.hospital}
                    </h4>
                    <div className="flex items-center space-x-2 text-gray-400 text-sm mt-1">
                      <MapPin className="w-4 h-4" />
                      <span>{job.location}</span>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1 px-3 py-1 bg-green-500/20 rounded-full">
                    <Star className="w-4 h-4 text-green-400" fill="currentColor" />
                    <span className="text-sm font-semibold text-green-400">{job.match}%</span>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm text-gray-400">Specialty</div>
                    <div className="text-white font-semibold">{job.specialty}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-400">Rate</div>
                    <div className="text-2xl font-bold text-gradient bg-gradient-to-r from-accent-400 to-primary-400">
                      ${job.rate}/hr
                    </div>
                  </div>
                </div>

                <motion.button
                  className="w-full mt-4 px-4 py-2 bg-gradient-to-r from-accent-500 to-primary-500 rounded-xl text-white font-semibold opacity-0 group-hover:opacity-100 transition-opacity"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Apply Now
                </motion.button>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}
